import type { Chunk } from './chunk';
import { isWalkable, canTraverseElevation } from './chunk';

export interface Point { x: number; y: number; }

interface Node {
  x: number;
  y: number;
  g: number;
  f: number;
  parent: Node | null;
}

// 8-direction movement; diagonal cost = sqrt(2).
const NEIGHBORS = [
  { dx: 1,  dy: 0,  cost: 1 },
  { dx: -1, dy: 0,  cost: 1 },
  { dx: 0,  dy: 1,  cost: 1 },
  { dx: 0,  dy: -1, cost: 1 },
  { dx: 1,  dy: 1,  cost: 1.4142 },
  { dx: 1,  dy: -1, cost: 1.4142 },
  { dx: -1, dy: 1,  cost: 1.4142 },
  { dx: -1, dy: -1, cost: 1.4142 },
];

function heuristic(ax: number, ay: number, bx: number, by: number): number {
  const dx = Math.abs(ax - bx);
  const dy = Math.abs(ay - by);
  return Math.max(dx, dy) + 0.4 * Math.min(dx, dy);
}

export function findPath(chunk: Chunk, start: Point, goal: Point): Point[] {
  if (!isWalkable(chunk, goal.x, goal.y)) {
    // Snap to nearest walkable tile within a small radius — clicking onto
    // water/walls shouldn't silently fail.
    const snapped = nearestWalkable(chunk, goal);
    if (!snapped) return [];
    goal = snapped;
  }

  const open: Node[] = [];
  const cameFrom = new Map<number, Node>();
  const gScore = new Map<number, number>();
  const key = (x: number, y: number) => y * chunk.width + x;

  const startNode: Node = { x: start.x, y: start.y, g: 0, f: heuristic(start.x, start.y, goal.x, goal.y), parent: null };
  open.push(startNode);
  gScore.set(key(start.x, start.y), 0);

  while (open.length > 0) {
    // Pop lowest-f node. (Tiny chunks → linear scan is fine; swap for a heap later.)
    let bestIdx = 0;
    for (let i = 1; i < open.length; i++) if (open[i].f < open[bestIdx].f) bestIdx = i;
    const cur = open.splice(bestIdx, 1)[0];

    if (cur.x === goal.x && cur.y === goal.y) {
      const path: Point[] = [];
      let n: Node | null = cur;
      while (n) { path.unshift({ x: n.x, y: n.y }); n = n.parent; }
      // Drop the start tile — we're already there.
      return path.slice(1);
    }

    for (const { dx, dy, cost } of NEIGHBORS) {
      const nx = cur.x + dx;
      const ny = cur.y + dy;
      if (!isWalkable(chunk, nx, ny)) continue;
      if (!canTraverseElevation(chunk, cur.x, cur.y, nx, ny)) continue;
      // Disallow corner-cutting through walls.
      if (dx !== 0 && dy !== 0) {
        if (!isWalkable(chunk, cur.x + dx, cur.y) || !isWalkable(chunk, cur.x, cur.y + dy)) continue;
      }
      const tentativeG = cur.g + cost;
      const k = key(nx, ny);
      if (tentativeG < (gScore.get(k) ?? Infinity)) {
        gScore.set(k, tentativeG);
        const node: Node = {
          x: nx,
          y: ny,
          g: tentativeG,
          f: tentativeG + heuristic(nx, ny, goal.x, goal.y),
          parent: cur,
        };
        cameFrom.set(k, node);
        open.push(node);
      }
    }
  }

  return [];
}

function nearestWalkable(chunk: Chunk, p: Point): Point | null {
  for (let r = 1; r <= 6; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.abs(dx) !== r && Math.abs(dy) !== r) continue;
        if (isWalkable(chunk, p.x + dx, p.y + dy)) return { x: p.x + dx, y: p.y + dy };
      }
    }
  }
  return null;
}
